/**
 * <compare-slider> — before/after reveal.
 *
 * Pointer Events cover mouse, touch and pen with one code path, and pointer
 * capture means a drag keeps working when the finger leaves the element.
 * Dragging writes a single custom property; the clip is done by CSS.
 */
(function () {
  'use strict';

  if (customElements.get('compare-slider')) return;

  class CompareSlider extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.compare-slider__frame');
      this.handle = this.querySelector('[data-compare-handle]');
      if (!this.frame || !this.handle) return;

      this.position = parseFloat(this.dataset.start) || 50;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      this.handle.addEventListener('pointerdown', this.onPointerDown);
      this.handle.addEventListener('keydown', (event) => this.onKeydown(event));

      // Clicking anywhere on the image jumps the handle there — quicker than
      // dragging from the middle every time.
      this.frame.addEventListener('pointerdown', (event) => {
        if (event.target.closest('[data-compare-handle]')) return;
        this.setFromClientX(event.clientX);
      });
    }

    disconnectedCallback() {
      this.handle?.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
    }

    onPointerDown(event) {
      event.preventDefault();
      this.handle.setPointerCapture?.(event.pointerId);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp, { once: true });
      this.dragging = true;
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      this.setFromClientX(event.clientX);
    }

    onPointerUp() {
      this.dragging = false;
      window.removeEventListener('pointermove', this.onPointerMove);
    }

    onKeydown(event) {
      const step = event.shiftKey ? 10 : 2;
      let next = this.position;

      switch (event.key) {
        case 'ArrowLeft':
          next -= step;
          break;
        case 'ArrowRight':
          next += step;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = 100;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.set(next);
    }

    setFromClientX(clientX) {
      const rect = this.frame.getBoundingClientRect();
      if (!rect.width) return;
      this.set(((clientX - rect.left) / rect.width) * 100);
    }

    set(value) {
      this.position = Math.min(100, Math.max(0, value));
      this.style.setProperty('--compare-position', `${this.position}%`);
      this.handle.setAttribute('aria-valuenow', Math.round(this.position));
    }
  }

  customElements.define('compare-slider', CompareSlider);
})();
